import React from 'react';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';
import { Map } from 'immutable';

import { Container } from '../../../styles/Container';

import { color } from '../../../styles/helpers/variables';

import { SITE_ROOT_URL } from '../../../constants/endpoints';

import { fontFamilyHeading, fontFamilyBody } from '../../../styles/helpers/mixins';

class InfoSectionComponent extends React.Component {
  constructor(props) {
    super(props);
    this.innerContentRef = React.createRef();
    this.onHeaderClick = this.onHeaderClick.bind(this);
    this.onResize = this.onResize.bind(this);
    this.setHeight = this.setHeight.bind(this);
    this.state = {
      active: this.props.type === 'description',
      height: 0
    };
  }

  componentDidMount() {
    this.setHeight();
    window.addEventListener('resize', this.onResize);
  }

  componentDidUpdate(prevProps) {
    if (this.props.children !== prevProps.children) {
      this.setHeight();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.onResize);
  }

  onResize() {
    this.setHeight();
  }

  onHeaderClick() {
    if (window.innerWidth < 960) {
      this.setState({ active: !this.state.active });
    }
  }

  onHeaderLinkClick(event) {
    if (window.innerWidth < 960) {
      event.preventDefault();
    }
  }

  setHeight() {
    this.setState({ height: this.innerContentRef.current.clientHeight });
  }

  render() {
    const { children, type, name, url, imageUrl, imageAlt } = this.props;
    const { active, height } = this.state;

    if (!children) {
      return null;
    }

    return (
      <InfoSection type={type}>
        <InfoSectionHeader
          active={active}
          onClick={this.onHeaderClick}
          role={window.innerWidth < 960 ? 'button' : undefined}
        >
          {imageUrl && (
            <InfoSectionHeaderImage type={type}>
              {url ? (
                <a href={url} onClick={this.onHeaderLinkClick}>
                  <img src={imageUrl} alt={imageAlt} />
                </a>
              ) : (
                <img src={imageUrl} alt={imageAlt} />
              )}
            </InfoSectionHeaderImage>
          )}
          <InfoSectionHeaderTitle>
            {url ? (
              <a href={url} onClick={this.onHeaderLinkClick}>
                {name}
              </a>
            ) : (
              name
            )}
          </InfoSectionHeaderTitle>
        </InfoSectionHeader>
        <InfoSectionContent active={active} style={{ height }}>
          <InfoSectionContentInner innerRef={this.innerContentRef}>{children}</InfoSectionContentInner>
        </InfoSectionContent>
      </InfoSection>
    );
  }
}

InfoSectionComponent.defaultProps = {
  children: null,
  type: '',
  name: '',
  url: '',
  imageUrl: '',
  imageAlt: ''
};

InfoSectionComponent.propTypes = {
  children: PropTypes.node,
  type: PropTypes.string,
  name: PropTypes.string,
  url: PropTypes.string,
  imageUrl: PropTypes.string,
  imageAlt: PropTypes.string
};

const ProductPageInfo = ({ product }) => {
  const variantId = product.get('defaultVariantId');
  const variant = product
    .get('variants')
    .filter(item => item.get('id') === variantId)
    .get(0);
  return (
    <Container productPage>
      <InfoList>
        {window.innerWidth < 960 && (
          <InfoListItem>
            <InfoSectionComponent type="description" name="Beskrivning">
              <p dangerouslySetInnerHTML={{ __html: product.get('description') }} />
            </InfoSectionComponent>
          </InfoListItem>
        )}
        <InfoListItem>
          <InfoSectionComponent
            type="specification"
            name="Specifikationer"
            imageUrl="/images/icons/ruler.svg"
            imageAlt="Specifikationer"
          >
            <InfoSectionList>
              <InfoSectionListItem>
                <InfoSectionListItemLabel>Artikelnummer:</InfoSectionListItemLabel> {variant.get('id')}
              </InfoSectionListItem>
              <InfoSectionListItem>
                <InfoSectionListItemLabel>Varumärke:</InfoSectionListItemLabel>{' '}
                <a href={variant.getIn(['brand', 'url'])}>{variant.getIn(['brand', 'name'])}</a>
              </InfoSectionListItem>
              <InfoSectionListItem>
                <InfoSectionListItemLabel>Formgivare:</InfoSectionListItemLabel>{' '}
                <a href={variant.getIn(['designer', 'url'])}>{variant.getIn(['designer', 'name'])}</a>
              </InfoSectionListItem>
              {(variant.get('specifications') || []).map(
                specification =>
                  specification.get('value') && (
                    <InfoSectionListItem key={`info-section-list-item-${specification.get('key')}`}>
                      <InfoSectionListItemLabel>{specification.get('key')}:</InfoSectionListItemLabel>{' '}
                      {specification.get('value')}
                    </InfoSectionListItem>
                  )
              )}
            </InfoSectionList>
          </InfoSectionComponent>
        </InfoListItem>
        <InfoListItem>
          <InfoSectionComponent
            type="brand"
            name={variant.getIn(['brand', 'name'])}
            url={variant.getIn(['brand', 'url'])}
            imageUrl={`${SITE_ROOT_URL}/${variant.getIn(['brand', 'images', 0, 'url'])}`}
            imageAlt={variant.getIn(['brand', 'images', 0, 'alt'])}
          >
            <LineClampedParagraph>{variant.getIn(['brand', 'description'])}</LineClampedParagraph>
            <InfoSectionReadMore>
              <a href={variant.getIn(['brand', 'url'])}>Läs mer</a>
            </InfoSectionReadMore>
          </InfoSectionComponent>
        </InfoListItem>
        {variant.getIn(['designer', 'name']) &&
          variant.getIn(['designer', 'url']) &&
          variant.getIn(['designer', 'images', 0, 'url']) && (
            <InfoListItem>
              <InfoSectionComponent
                type="designer"
                name={variant.getIn(['designer', 'name'])}
                url={variant.getIn(['designer', 'url'])}
                imageUrl={`${SITE_ROOT_URL}/${variant.getIn(['designer', 'images', 0, 'url'])}`}
                imageAlt={variant.getIn(['designer', 'images', 0, 'alt'])}
              >
                <LineClampedParagraph>{variant.getIn(['designer', 'description'])}</LineClampedParagraph>
                <InfoSectionReadMore>
                  <a href={variant.getIn(['designer', 'url'])}>Läs mer</a>
                </InfoSectionReadMore>
              </InfoSectionComponent>
            </InfoListItem>
          )}
      </InfoList>
    </Container>
  );
};

ProductPageInfo.defaultProps = {
  product: Map(),
};

ProductPageInfo.propTypes = {
  product: PropTypes.instanceOf(Map),
};

export default ProductPageInfo;

const InfoList = styled.div`
  display: flex;
  flex-wrap: wrap;
  @media screen and (min-width: 960px) {
    margin: 0 -40px 0 0;
  }
  @media screen and (min-width: 1440px) {
    margin: 0 -60px 0 0;
  }
  @media print {
    display: none;
  }
`;

const InfoListItem = styled.div`
  flex: 0 0 100%;
  max-width: 100%;
  margin: 0 0 10px;
  @media screen and (min-width: 960px) {
    flex: 0 0 33.333%;
    max-width: 33.333%;
    margin: 0;
    padding: 0 40px 0 0;
  }
  @media screen and (min-width: 1440px) {
    flex: 0 0 37.5%;
    max-width: 37.5%;
    padding: 0 60px 0 0;
    &:first-child {
      flex: 0 0 25%;
      max-width: 25%;
    }
  }
  &:last-child {
    margin: 0;
  }
`;

const InfoSection = styled.div`
  background-color: ${color.white};
  @media screen and (min-width: 960px) {
    background-color: transparent;
  }
`;

const InfoSectionHeader = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  height: 60px;
  padding: 10px 50px 10px 20px;
  cursor: pointer;
  @media screen and (min-width: 960px) {
    height: 50px;
    margin: 0 0 20px;
    padding: 0;
    cursor: default;
  }
  &:after {
    content: '';
    position: absolute;
    top: 50%;
    right: 20px;
    height: 10px;
    width: 10px;
    background-image: url('/images/icons/arrow.svg');
    background-position: center;
    background-repeat: no-repeat;
    background-size: contain;
    transform: translateY(-50%);
    ${props =>
      props.active &&
      css`
        transform: translateY(-50%) rotate(180deg);
      `};
    @media screen and (min-width: 960px) {
      display: none;
    }
  }
`;

const InfoSectionHeaderImage = styled.div`
  flex: 0 0 auto;
  position: relative;
  height: 40px;
  width: 40px;
  margin: 0 10px 0 0;
  border-radius: 50%;
  background-color: ${color.white};
  color: transparent;
  overflow: hidden;
  @media screen and (min-width: 960px) {
    height: 50px;
    width: 50px;
  }
  ${props =>
    props.type === 'brand' &&
    css`
      > img,
      > a > img {
        padding: 5px;
      }
    `};
  ${props =>
    props.type === 'specification' &&
    css`
      display: none;
      @media screen and (min-width: 960px) {
        display: block;
      }
      > img,
      > a > img {
        padding: 7.5px;
      }
    `};
  > a {
    color: inherit;
  }
  > img,
  > a > img {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    object-fit: contain;
  }
`;

const InfoSectionHeaderTitle = styled.h2`
  margin: 0;
  color: ${color.black};
  ${fontFamilyHeading};
  font-size: 14px;
  font-weight: 400;
  text-transform: uppercase;
  line-height: 1.25;
  letter-spacing: 0.1125em;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  @media screen and (min-width: 960px) {
    font-size: 17px;
  }
  > a {
    color: inherit;
    text-decoration: none;
  }
`;

const InfoSectionContent = styled.div`
  ${fontFamilyBody}
  font-size: 14px;
  font-weight: normal;
  line-height: 1.57;
  color: ${color.black};
  overflow: hidden;
  transition: height 0.125s ease;
  ${props =>
    !props.active &&
    css`
      height: 0 !important;
    `};
  @media screen and (min-width: 960px) {
    height: auto !important;
    overflow: visible;
    transition: none;
  }
`;

const InfoSectionReadMore = styled.div`
  > a {
    color: ${color.black};
    text-decoration: underline;
  }
`;

const InfoSectionContentInner = styled.div`
  padding: 0 20px 20px;
  @media screen and (min-width: 960px) {
    padding: 0;
  }
  > p {
    margin: 0 0 10px;
    &:last-child {
      margin: 0;
    }
    + ${InfoSectionReadMore} {
      margin: -5px 0 0;
      @media screen and (min-width: 960px) {
        margin: 0;
      }
    }
  }
`;

const InfoSectionList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const LineClampedParagraph = styled.p`
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 12;
  -webkit-box-orient: vertical;
`;

const InfoSectionListItem = styled.li`
  margin: 0 0 10px;
  &:last-child {
    margin: 0;
  }
  > a {
    color: ${color.black};
    text-decoration: underline;
  }
`;

const InfoSectionListItemLabel = styled.span`
  font-weight: 600;
`;



// WEBPACK FOOTER //
// ./src/components/productPage/ProductPageInfo/ProductPageInfo.js